import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileCheck2, Loader2, Upload, X } from 'lucide-react';
import { Badge, Button, Card, CardContent, CardHeader, CardTitle } from '@mlm/ui';
import {
  api,
  getKycRequirements,
  restoreSession,
  submitKyc,
  uploadKycDocument,
  type KycDocumentRef,
  type KycRequirement,
} from '../../lib/api';

type KycSubmission = {
  id: string;
  status: 'submitted' | 'verified' | 'rejected';
  documents: Array<{ kind: string; r2Key: string }>;
  rejectionReason: string | null;
  createdAt: string;
};

type SlotState = {
  file: File | null;
  previewUrl: string | null;
  r2Key: string | null;
  uploading: boolean;
  error: string | null;
};

const emptySlot = (): SlotState => ({
  file: null,
  previewUrl: null,
  r2Key: null,
  uploading: false,
  error: null,
});

export function KycTab() {
  const qc = useQueryClient();
  const [slots, setSlots] = useState<Record<string, SlotState>>({});

  const myKyc = useQuery({
    queryKey: ['my-kyc'],
    queryFn: async () => (await api.get<KycSubmission | null>('/me/kyc')).data,
  });

  // Admin-defined document slots (front/back/selfie out of the box, fully
  // configurable from Cash-out settings).
  const requirementsQ = useQuery({
    queryKey: ['kyc-requirements'],
    queryFn: getKycRequirements,
  });
  const requirements: KycRequirement[] = requirementsQ.data ?? [];

  const setSlot = (kind: string, patch: Partial<SlotState>) =>
    setSlots((s) => ({ ...s, [kind]: { ...(s[kind] ?? emptySlot()), ...patch } }));

  async function onPick(kind: string, file: File | null) {
    if (!file) return;
    const previewUrl = URL.createObjectURL(file);
    setSlot(kind, { file, previewUrl, r2Key: null, uploading: true, error: null });
    try {
      const { r2Key } = await uploadKycDocument(file);
      setSlot(kind, { r2Key, uploading: false });
    } catch (err) {
      const e = err as { response?: { data?: { error?: string } } };
      setSlot(kind, { uploading: false, error: e.response?.data?.error ?? 'upload_failed' });
    }
  }

  function clearSlot(kind: string) {
    const cur = slots[kind];
    if (cur?.previewUrl) URL.revokeObjectURL(cur.previewUrl);
    setSlot(kind, emptySlot());
  }

  const submitMut = useMutation({
    meta: { successMessage: 'KYC documents submitted for review.' },
    mutationFn: async () => {
      const documents: KycDocumentRef[] = requirements.flatMap((d) => {
        const key = slots[d.key]?.r2Key;
        return key ? [{ kind: d.key, r2Key: key }] : [];
      });
      return submitKyc(documents);
    },
    onSuccess: async () => {
      // Reset slots; the status query now reflects "submitted".
      setSlots({});
      qc.invalidateQueries({ queryKey: ['my-kyc'] });
      await restoreSession();
    },
  });

  const status = myKyc.data?.status;
  const requiredReady = requirements.filter((d) => d.required).every((d) => !!slots[d.key]?.r2Key);
  const anyUploading = requirements.some((d) => slots[d.key]?.uploading);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <FileCheck2 className="h-4 w-4 text-primary" /> KYC verification
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-2 text-sm">
          <span>Status:</span>
          {status === 'verified' && <Badge>Verified</Badge>}
          {status === 'submitted' && <Badge variant="secondary">Pending review</Badge>}
          {status === 'rejected' && <Badge variant="destructive">Rejected</Badge>}
          {!status && <Badge variant="outline">Not submitted</Badge>}
        </div>

        {myKyc.data?.rejectionReason && (
          <p className="text-xs text-destructive">Reason: {myKyc.data.rejectionReason}</p>
        )}

        <p className="text-xs text-muted-foreground">
          Verifying your identity is required before you can request payouts. Upload clear photos of
          your documents and our staff will review them.
        </p>

        {status !== 'verified' && (
          <>
            <div className="grid gap-3 sm:grid-cols-3">
              {requirements.map((d) => (
                <DocSlot
                  key={d.key}
                  label={d.required ? d.label : `${d.label} (optional)`}
                  description={d.description}
                  state={slots[d.key] ?? emptySlot()}
                  onPick={(f) => onPick(d.key, f)}
                  onClear={() => clearSlot(d.key)}
                />
              ))}
            </div>
            <Button
              onClick={() => submitMut.mutate()}
              disabled={!requiredReady || anyUploading || submitMut.isPending}
              size="sm"
            >
              {submitMut.isPending
                ? 'Submitting…'
                : status === 'submitted' || status === 'rejected'
                  ? 'Re-submit for review'
                  : 'Submit for review'}
            </Button>
            {!requiredReady && (
              <p className="text-[11px] text-muted-foreground">
                Upload all required documents to submit.
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function DocSlot({
  label,
  description,
  state,
  onPick,
  onClear,
}: {
  label: string;
  description?: string;
  state: SlotState;
  onPick: (file: File | null) => void;
  onClear: () => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <div className="space-y-1.5">
      <label className="text-xs text-muted-foreground">{label}</label>
      {description && <p className="text-[10px] text-muted-foreground/80">{description}</p>}
      <div className="relative aspect-[4/3] overflow-hidden rounded-md border-2 border-dashed border-muted-foreground/30">
        {state.previewUrl ? (
          <>
            <img src={state.previewUrl} alt={label} className="h-full w-full object-cover" />
            {state.uploading && (
              <div className="absolute inset-0 flex items-center justify-center bg-background/60">
                <Loader2 className="h-5 w-5 animate-spin text-primary" />
              </div>
            )}
            <button
              type="button"
              onClick={onClear}
              className="absolute right-1 top-1 rounded-full bg-background/80 p-1 text-foreground hover:bg-background"
              aria-label="Remove"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </>
        ) : (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="flex h-full w-full flex-col items-center justify-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          >
            <Upload className="h-5 w-5" />
            Tap to upload
          </button>
        )}
      </div>
      {state.error && (
        <p className="text-[11px] text-destructive">{state.error.replace(/_/g, ' ')}</p>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => onPick(e.target.files?.[0] ?? null)}
      />
    </div>
  );
}
